import { useTranslation } from "react-i18next"

import { useNotification } from "@/contexts/notification-context"
import { ApiQueryName } from "@/models/api"
import { useRenameQueryResult } from "@/services/utils"
import { createQueryKey } from "@/services/utils/createQueryKey"
import { httpClient, toHttpError } from "@/utils/api"
import type { SuccessData } from "@/utils/api"
import { getErrorNotificationMessage } from "@/utils/notification-bridge"
import { useMutation, useQuery } from "@tanstack/react-query"

export interface TokenUser {
  email: string
  id: string
}

interface GetUserByPasswordResetTokenData extends SuccessData {
  user: TokenUser
}

const getUserByPasswordResetToken = async (
  token: string
): Promise<TokenUser | null> => {
  try {
    const response = await httpClient.get<GetUserByPasswordResetTokenData>(
      `/api/v1/password_reset/${token}`
    )

    return response.data.success ? response.data.user : null
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

const updateUserPassword = async (
  token: string,
  params: { password: string }
): Promise<SuccessData | null> => {
  try {
    const response = await httpClient.patch<SuccessData>(
      `/api/v1/password_reset/${token}`,
      params
    )

    return response.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

export const useGetUserByPasswordResetToken = (token: string | undefined) => {
  const queryKey = createQueryKey(ApiQueryName.PasswordUser, "getOne", null, {
    token: token ?? "",
  })

  const queryResult = useQuery({
    queryKey,
    queryFn: () => getUserByPasswordResetToken(token ?? ""),
    enabled: Boolean(token),
  })

  return useRenameQueryResult(queryResult, ApiQueryName.PasswordUser)
}

export const useUpdateUserPassword = () => {
  const { setNotification } = useNotification()
  const { t } = useTranslation("common")

  const mutationResult = useMutation({
    meta: { handlesOwnErrors: true },
    mutationFn: async ({
      token,
      password,
    }: {
      token: string
      password: string
    }) => {
      const data = await updateUserPassword(token, { password })

      if (!data?.success) {
        throw toHttpError(new Error("Failed to update password"))
      }

      return data
    },
    onError: (error) => {
      setNotification({
        message: getErrorNotificationMessage(
          error,
          t("resetPassword.updatePasswordError")
        ),
        type: "error",
      })
    },
  })

  return useRenameQueryResult(mutationResult, ApiQueryName.PasswordUpdate)
}
